import React from 'react';

import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { buildTokens, type Theme } from './buildTokens';
import type { BrandingSettings } from '../../model/client/ClientSettings';

// Provides the tenant-resolved design tokens (base tokens + branding overrides)
// to the component tree. Must be rendered inside ClientConfigProvider so the
// tenant branding is available. Components call useTokens() to read colours /
// spacing instead of reaching into useClientConfig().branding directly.
const TokensContext = React.createContext<Theme | undefined>(undefined);

export const TokensProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { branding } = useClientConfig();
  const theme = React.useMemo(() => buildTokens(branding), [branding]);

  return <TokensContext.Provider value={theme}>{children}</TokensContext.Provider>;
};

// Base (unbranded) design tokens. buildTokens folds branding onto the base
// tokens; with no overrides (every field absent) it returns the base set. Used
// where there is no tenant context to resolve branding from — see
// StaticTokensProvider below.
const defaultTheme: Theme = buildTokens({} as BrandingSettings);

// Supplies an explicit theme (default: the unbranded base tokens) WITHOUT
// requiring ClientConfigProvider. Use in token-less surfaces where the
// tenant-resolving TokensProvider isn't in scope: the /dev/primitives gallery
// (a public, no-auth dev route) and Tier-2 primitive unit tests. Consumers still
// call the same useTokens() hook, so primitive code is identical to production.
export const StaticTokensProvider: React.FC<{ theme?: Theme; children: React.ReactNode }> = ({
  theme = defaultTheme,
  children,
}) => <TokensContext.Provider value={theme}>{children}</TokensContext.Provider>;

export const useTokens = (): Theme => {
  const context = React.useContext(TokensContext);
  if (!context) {
    throw new Error('useTokens must be used within a TokensProvider');
  }
  return context;
};
